import { config } from "config";
import { partition } from "lodash";
import { channel } from "redux-saga";
import { delay, fork, put, select, take, takeEvery } from "redux-saga/effects";

import { timezone } from "constants/date";
import { GroupFilterEnum } from "constants/filters";
import { DELIVERY_ISSUES } from "constants/filters/delivery-status-filter";
import { QueryKeys } from "constants/query-keys";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { redirectToAccounts } from "helpers/routing";
import { ApiManager } from "services/api";
import { deserializeMacro } from "services/api/macros/macro-serializer";
import { TicketSerializer } from "services/api/ticket";
import { serializeFilters } from "services/api/ticket/ticket-api";
import WebSocketManagerService, { WebSocketManager } from "services/websocket";
import { IIncomingMessage } from "services/websocket/interfaces";
import WebSocketParser from "services/websocket/websocket-parser";
import { IEntityDeleteSuccess, RequestAction } from "store/entities/actions";
import { AgentActions } from "store/entities/agents";
import { BillingActions } from "store/entities/billing";
import { CannedResponseActions } from "store/entities/canned-responses";
import { ContactFormActions } from "store/entities/contact-forms/actions";
import { customFieldActions } from "store/entities/custom-fields/actions";
import { CustomViewsActions, CustomViewsActionsNames } from "store/entities/custom-views/actions";
import { selectCustomViewsAlphabetically } from "store/entities/custom-views/selectors";
import { EmailDomainActions } from "store/entities/email-domains";
import { EmailTemplateActions } from "store/entities/email-template/actions";
import { IntegrationsActions } from "store/entities/integrations";
import { ILicense, LicenseActions, selectLicense } from "store/entities/license";
import { IMacro } from "store/entities/macros/interfaces";
import { MailboxesActions } from "store/entities/mailboxes/actions";
import { ReplyAddressActions } from "store/entities/reply-addresses/actions";
import { RulesActions } from "store/entities/rules/actions";
import { SpamActions } from "store/entities/spam/actions";
import { selectBlockedEntriesLoading, selectTrustedEntriesLoading } from "store/entities/spam/selectors";
import { SubscriptionActions } from "store/entities/subscriptions";
import { TagActions } from "store/entities/tags";
import { TeamActions } from "store/entities/teams";
import { AppActionNames } from "store/features/app";
import { getAccessToken, getCurrentAgentId } from "store/features/session";
import { IActionWithPayload } from "store/helper";
import { AgentActivityEventType } from "store/views/reports";
import { TicketsViewActions, TicketsViewActionsNames } from "store/views/tickets/actions";
import { TicketAgentActivityType } from "store/views/tickets/interfaces";

import { queryClient } from "../../../../queryClient";
import { TicketActivityActions } from "../ticket-activity/actions";

let webSocketState = "unconnected";
const webSocketStateChan = channel();
const webSocketPushes = channel();
const webSocketResponses = channel();
const unregisterPushes = channel();
const authPushes = channel();

export const wsManager: WebSocketManager<string, string> = WebSocketManagerService(config.websocket);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* watchInit() {
  yield take(AppActionNames.READY);
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const accessToken = yield select(getAccessToken);

  wsManager.on("connect", () => {
    webSocketStateChan.put("connected");
    wsManager.emit(
      WebSocketParser.createMessage("auth", {
        token: `Bearer ${accessToken}`,
        deviceID: localStorage.getItem("deviceID") || undefined,
      })
    );
  });

  wsManager.on("message", (incomingMessage: string) => {
    const message = WebSocketParser.parse(incomingMessage);
    if (message && message.type === "push") {
      webSocketPushes.put(message);
    } else if (
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      (message.errorType === "auth" && message.errorDetails === "Unauthorized") ||
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      message.errorType === "tokenRevokedMigration" ||
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      message.errorType === "tokenRevoked"
    ) {
      authPushes.put(message);
    } else if (message && message.action === "auth" && message.target === "auth") {
      webSocketStateChan.put("authorized");
      webSocketResponses.put(message);
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      if (message.payload.deviceID) {
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        localStorage.setItem("deviceID", message.payload.deviceID);
      }
    } else if (message && message.type === "response") {
      webSocketResponses.put(message);
    }
  });

  wsManager.connect();
}

const ticketsSubscriptions: Record<string, TicketsRoutes> = {};
// @ts-expect-error TS(6133)
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- false negative! DO NOT REMOVE
let activitySubscription: string | null = null;

function ensureSingle<T>(arr: T[] | T): T {
  if (Array.isArray(arr)) {
    return arr[0];
  }
  return arr;
}

function ensureArray<T>(arr: T[] | T): T[] {
  if (Array.isArray(arr)) {
    return arr;
  }
  return [arr];
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* handleWebsocketPush(message: IIncomingMessage) {
  if (message.target === "tickets") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action !== "delete") {
      yield put(
        TicketsViewActions.onTicketUpdated(
          id,
          message.action,
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          payload.doc ? TicketSerializer.deserialize(payload.doc) : undefined
        )
      );
    }
  } else if (message.target === "agents") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(AgentActions.syncDelete({ id }));
    } else {
      yield put(AgentActions.fetch({ id }));
    }
  } else if (message.target === "teams") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(TeamActions.syncDelete({ id }));
      yield put(AgentActions.fetchCollection({}));
      yield put(TagActions.fetchCollection({}));
      yield put(CannedResponseActions.fetchCollection({}));
    } else if (message.action === "updateBatch") {
      yield put(TeamActions.fetchCollection({}));
    } else if (message.action === "create" || message.action === "update") {
      yield put(TeamActions.fetch({ id }));
      yield put(AgentActions.fetchCollection({}));
    } else {
      yield put(TeamActions.fetch({ id }));
    }
  } else if (message.target === "licenses") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    yield put(LicenseActions.fetch({ id }));
  } else if (message.target === "accounts:subscription") {
    yield put(SubscriptionActions.fetchCollection({}));
  } else if (message.target === "accounts:billing") {
    yield put(BillingActions.fetchBillingInfo());
  } else if (message.target === "tags") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(TagActions.syncDelete({ id }));
    } else {
      yield put(TagActions.fetch({ id }));
    }
  } else if (message.target === "emailDomains") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(EmailDomainActions.syncDelete({ id }));
    } else {
      yield put(EmailDomainActions.fetch({ id }));
    }
  } else if (message.target === "cannedResponses") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(CannedResponseActions.syncDelete({ id }));
    } else {
      yield put(CannedResponseActions.fetch({ id }));
    }
  } else if (message.target === "integrations") {
    yield put(IntegrationsActions.fetchCollection({}));
  } else if (message.target === "macros") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;

    if (message.action === "delete") {
      // @ts-expect-error TS(2345)
      queryClient.setQueryData([QueryKeys.Macros], (prev: IMacro[]) => prev.filter(macro => macro.id !== id));
    } else {
      const { result, error } = yield ApiManager.macrosApi.fetch(id);
      if (error) {
        throw error;
      }
      // @ts-expect-error TS(2345)
      queryClient.setQueryData([QueryKeys.Macros], (prev: IMacro[]) => {
        const newMacro = deserializeMacro(result);
        const macroExists = prev.some(macro => macro.id === newMacro.id);
        if (macroExists) {
          return prev.map(macro => (macro.id === newMacro.id ? newMacro : macro));
        }
        return [...prev, newMacro];
      });
    }
  } else if (message.target === "sources") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    yield put(
      TicketsViewActions.setNavigationItemCount({
        route: ticketsSubscriptions[id],
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        count: ensureSingle(message.payload).value.count,
      })
    );
  } else if (message.target === "replyAddresses") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(ReplyAddressActions.syncDelete({ id }));
    } else {
      yield put(ReplyAddressActions.fetch({ id }));
    }
  } else if (message.target === "rules") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(RulesActions.syncDelete({ id }));
    } else {
      yield put(RulesActions.fetch({ id }));
    }
  } else if (message.target === "mailboxes") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(MailboxesActions.syncDelete({ id }));
    } else {
      yield put(MailboxesActions.fetch({ id }));
    }
  } else if (message.target === "views") {
    const payload = ensureSingle(message.payload);
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = payload.ID;
    if (message.action === "delete") {
      yield put(CustomViewsActions.syncDelete({ id }));
    } else {
      yield put(CustomViewsActions.fetch({ id }));
    }
  } else if (message.target === "agentActions") {
    // @ts-expect-error TS(2339): Property 'type' does not exist on type 'ISinglePay... Remove this comment to see the full error message
    const { type: activityType, agentID, ticketID } = ensureSingle(message.payload);
    switch (activityType) {
      case TicketAgentActivityType.TypingStart:
        yield put(TicketActivityActions.setAgentTyping(agentID, ticketID));
        break;
      case TicketAgentActivityType.TypingStop:
        yield put(TicketActivityActions.unsetAgentTyping(agentID, ticketID));
        break;
      case TicketAgentActivityType.ViewingStart:
        yield put(TicketActivityActions.setAgentViewing(agentID, ticketID));
        break;
      case TicketAgentActivityType.ViewingStop:
        yield put(TicketActivityActions.unsetAgentViewing(agentID, ticketID));
        break;
      default:
    }
  } else if (message.target === "templates") {
    if (message.action !== "update") {
      yield put(EmailTemplateActions.fetch());
    }
  } else if (message.target === "rulesCounters") {
    if (message.action === "update") {
      const updatePayload = ensureArray(message.payload);
      yield put(RulesActions.updateCounters(updatePayload as { ID: string; useCounter: number }[]));
    }
  } else if (message.target === "trustedEmails") {
    if (message.action === "delete") {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const id = ensureSingle(message.payload).ID;
      yield put(SpamActions.syncTrustedDelete({ id }));
    } else {
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      const listLoading = yield select(selectTrustedEntriesLoading);

      if (!listLoading) {
        yield put(SpamActions.fetchTrustedList());
      }
    }
  } else if (message.target === "blockedEmails") {
    if (message.action === "delete") {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const id = ensureSingle(message.payload).ID;
      yield put(SpamActions.syncBlockedDelete({ id }));
    } else {
      // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
      const listLoading = yield select(selectBlockedEntriesLoading);

      if (!listLoading) {
        yield put(SpamActions.fetchBlockedList());
      }
    }
  } else if (message.target === "contactForms") {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const id = ensureSingle(message.payload).ID;
    if (message.action === "delete") {
      yield put(ContactFormActions.syncDelete({ id }));
    } else {
      yield put(ContactFormActions.fetch({ id }));
    }
  } else if (message.target === "customFields") {
    if (message.action === "update" || message.action === "create" || message.action === "delete") {
      yield put(customFieldActions.fetchCollection());
    }
  } else if (message.target === "asyncTasks") {
    if (message.action === "done" || message.action === "error") {
      yield put(
        IntegrationsActions.liveChatSelectiveImportFinished({
          action: message.action,
          error: ensureSingle(message.payload)?.error,
        })
      );
    }

    if (message.action === "update") {
      const payload = ensureSingle(message.payload);
      if (payload?.doc?.fetchEntities) {
        yield put(AgentActions.fetchCollection({}));
        yield put(TeamActions.fetchCollection({}));
        yield put(TagActions.fetchCollection({}));
        yield put(CannedResponseActions.fetchCollection({}));
      }
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const constantSubscriptions = (currentAgentId: string) => [
  {
    requestID: TicketsRoutes.Unassigned,
    type: "tickets",
    query: {
      status: "open",
      hasAssignee: false,
      assignment: {
        team: {
          ID: GroupFilterEnum.MyTeams,
        },
      },
    },
  },
  {
    requestID: TicketsRoutes.MyOpen,
    type: "tickets",
    query: {
      status: "open",
      assignment: {
        agent: {
          ID: currentAgentId,
        },
      },
    },
  },
  {
    requestID: TicketsRoutes.Open,
    type: "tickets",
    query: {
      status: "open",
    },
  },
  {
    requestID: TicketsRoutes.Pending,
    type: "tickets",
    query: {
      status: "pending",
    },
  },
  {
    requestID: TicketsRoutes.OnHold,
    type: "tickets",
    query: {
      status: "onhold",
    },
  },
  {
    requestID: TicketsRoutes.Undelivered,
    type: "tickets",
    query: {
      deliveryStatus: DELIVERY_ISSUES,
      deliveryUpdatedAtFrom: "-7D",
    },
  },
];

// @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
const CONSTANT_SUBSCRIPTIONS_COUNT = constantSubscriptions(null).length;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* handleWebsocketResponse(message: IIncomingMessage) {
  if (message.action === "auth" && message.target === "auth") {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const currentAgentId = yield select(getCurrentAgentId);
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const customViews = yield select(selectCustomViewsAlphabetically);
    const license: ILicense = yield select(selectLicense);

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customViewSubscriptions = customViews.map((view: any) => {
      const filters = serializeFilters(view.filters);

      if (filters.spam === undefined) {
        filters.spam = "false";
      }
      if (filters.timezone === undefined) {
        filters.timezone = timezone;
      }

      return {
        requestID: view.id,
        type: "tickets",
        query: filters,
      };
    });

    const allSubscriptions = [...constantSubscriptions(currentAgentId), ...customViewSubscriptions].slice(
      0,
      // @ts-expect-error TS(2345)
      license.settings.maxSubscriptionsPerSocket
    );

    allSubscriptions.forEach(subscription => {
      wsManager.emit(WebSocketParser.createMessage("subscribe", subscription));
    });
    wsManager.emit(WebSocketParser.createMessage("subscribe", { type: "agentActions" }));
  } else if (message.action === "unsubscribe" && message.target === "sources") {
    unregisterPushes.put({});
  } else if (message.action === "subscribe" && message.target === "sources") {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    ticketsSubscriptions[ensureSingle(message.payload).ID] = message.requestID as TicketsRoutes;
  } else if (message.action === "unsubscribe" && message.target === "agentActions") {
    activitySubscription = null;
  } else if (message.action === "subscribe" && message.target === "agentActions") {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    activitySubscription = ensureSingle(message.payload).ID;
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* reregisterCustomViews() {
  if (wsManager) {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const customViews = yield select(selectCustomViewsAlphabetically);

    const license: ILicense = yield select(selectLicense);

    const [subscribedCustomViews, unsubscribedCustomViews] = partition(customViews, view =>
      Object.values(ticketsSubscriptions).includes(view.id)
    );

    subscribedCustomViews.forEach(view => {
      const subscriptionID = Object.keys(ticketsSubscriptions).find(id => ticketsSubscriptions[id] === view.id);
      if (subscriptionID) {
        wsManager.emit(WebSocketParser.createMessage("unsubscribe", { ID: subscriptionID }));
        delete ticketsSubscriptions[subscriptionID];
      }
    });

    const subscriptionsToSubscribe = [
      // @ts-expect-error TS(18047)
      ...subscribedCustomViews.slice(0, license.settings.maxSubscriptionsPerSocket - CONSTANT_SUBSCRIPTIONS_COUNT),
      ...unsubscribedCustomViews.slice(
        0,
        Math.max(
          0,
          // @ts-expect-error TS(18047)
          license.settings.maxSubscriptionsPerSocket - CONSTANT_SUBSCRIPTIONS_COUNT - subscribedCustomViews.length
        )
      ),
    ];

    for (let i = 0; i < subscriptionsToSubscribe.length; i += 1) {
      yield take(unregisterPushes);
      const view = subscriptionsToSubscribe[i];
      const subscription = {
        requestID: view.id,
        type: "tickets",
        query: { ...serializeFilters(view.filters), spam: false, timezone },
      };
      wsManager.emit(WebSocketParser.createMessage("subscribe", subscription));
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function unregisterCustomView(action: IActionWithPayload<string, IEntityDeleteSuccess>) {
  const { id: targetID } = action.payload;

  const subscriptionID = Object.keys(ticketsSubscriptions).find(id => ticketsSubscriptions[id] === targetID);
  if (subscriptionID) {
    wsManager.emit(WebSocketParser.createMessage("unsubscribe", { ID: subscriptionID }));
    delete ticketsSubscriptions[subscriptionID];
  }
}

const WEB_SOCKET_POLLING_INTERVAL_MSEC = 250;
const WEB_SOCKET_POLLING_MAX_TRIES = 20;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* waitForAuthorization() {
  let tries = 0;
  while (tries < WEB_SOCKET_POLLING_MAX_TRIES) {
    if (webSocketState === "authorized") {
      return true;
    }

    yield delay(WEB_SOCKET_POLLING_INTERVAL_MSEC);
    tries += 1;
  }

  return false;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* pushTicketActivity(
  action: IActionWithPayload<string, { ticketID: string; agentActivity: AgentActivityEventType }>
) {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const isAuthorized = yield waitForAuthorization();

  if (isAuthorized) {
    const { ticketID, agentActivity } = action.payload;

    wsManager.emit(WebSocketParser.createMessage("notify", { ticketID, event: agentActivity }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* handleSignOut(_: IIncomingMessage) {
  yield redirectToAccounts();
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function watchSocketState(state: string) {
  webSocketState = state;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* websocketSagas() {
  yield fork(watchInit);
  yield takeEvery(webSocketStateChan, watchSocketState);
  yield takeEvery(webSocketPushes, handleWebsocketPush);
  yield takeEvery(webSocketResponses, handleWebsocketResponse);
  yield takeEvery(authPushes, handleSignOut);
  yield takeEvery(CustomViewsActionsNames.FETCH_SINGLE[RequestAction.SUCCESS], reregisterCustomViews);
  yield takeEvery(CustomViewsActionsNames.CREATE[RequestAction.SUCCESS], reregisterCustomViews);
  yield takeEvery(CustomViewsActionsNames.UPDATE[RequestAction.SUCCESS], reregisterCustomViews);
  yield takeEvery(CustomViewsActionsNames.DELETE[RequestAction.SUCCESS], unregisterCustomView);
  yield takeEvery(CustomViewsActionsNames.FETCH_COLLECTION[RequestAction.SUCCESS], reregisterCustomViews);
  yield takeEvery(TicketsViewActionsNames.NOTIFY_TICKET_ACTIVITY, pushTicketActivity);
}
